import { ChartConfiguration } from 'chart.js';
import moment from 'moment';
import { ConversionWirePeriod } from '@partner/feature-modules/dashboard/domain/model/conversion-metrics.model';
import {
  DashboardDateRange,
  DashboardPeriod,
  IsoDate,
} from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import {
  TrendBucket,
  TrendBucketUnit,
  TrendChartVm,
  TrendMetrics,
  TrendSeries,
  TrendTiles,
  TrendTileVm,
} from '@partner/feature-modules/dashboard/domain/model/trend-metrics.model';
import { formatDateRange } from './dashboard-filters.helper';
import {
  NO_VALUE_LABEL,
  percentLabel,
  TREND_BAND_EDGE_PERCENT,
  TrendDirection,
  trendTier,
} from './pipeline-funnel.helper';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const DAY_RANGE_SEPARATOR = '–';

export const TREND_TILE_ORDER: readonly (keyof TrendTiles)[] = ['submitted', 'approved', 'funded', 'avgDaysToFund'];

export const TREND_TILE_NAMES: Record<keyof TrendTiles, string> = {
  submitted: 'Submitted',
  approved: 'Approved',
  funded: 'Funded',
  avgDaysToFund: 'Avg. days to fund',
};

export const TREND_TILE_DIRECTIONS: Record<keyof TrendTiles, TrendDirection> = {
  submitted: TrendDirection.HIGHER_IS_BETTER,
  approved: TrendDirection.HIGHER_IS_BETTER,
  funded: TrendDirection.HIGHER_IS_BETTER,
  avgDaysToFund: TrendDirection.LOWER_IS_BETTER,
};

/** Zero and the band edge: a label never reads as flat, or as on the edge, when the change is not. */
export const TREND_CHANGE_LABEL_EDGES: readonly number[] = [0, TREND_BAND_EDGE_PERCENT];

/** The server charts at most this many month buckets of a Custom range. */
export const MAX_TREND_CHART_BUCKETS = 24;

/** Past this many buckets a month name repeats, so month labels carry their year. */
export const MONTH_LABEL_YEAR_THRESHOLD = 12;

export const TREND_CHART_TITLE = 'Submitted · Approved · Funded';

export const TREND_CHART_EMPTY_MESSAGE = 'Nothing was submitted, approved or funded in this range.';

/** A truncated chart holds only the range's tail, so an empty one says nothing about the tiles. */
export const TREND_CHART_TRUNCATED_EMPTY_MESSAGE = `Nothing was submitted, approved or funded in the last ${MAX_TREND_CHART_BUCKETS} months of this range.`;

export const TREND_SERIES: readonly TrendSeries[] = [TrendSeries.SUBMITTED, TrendSeries.APPROVED, TrendSeries.FUNDED];

export const TREND_SERIES_LABELS: Record<TrendSeries, string> = {
  [TrendSeries.SUBMITTED]: 'Submitted',
  [TrendSeries.APPROVED]: 'Approved',
  [TrendSeries.FUNDED]: 'Funded',
};

// Chart-semantic fills, the funnel palette's submitted / approved / funded stops. Declared here because the canvas
// cannot read SCSS; the legend's SCSS map mirrors them and a spec pins the two equal.
export const TREND_SERIES_COLORS: Record<TrendSeries, string> = {
  [TrendSeries.SUBMITTED]: '#C2C9BD',
  [TrendSeries.APPROVED]: '#B2F2B3',
  [TrendSeries.FUNDED]: '#2CA64A',
};

/** Tick ink and type for the canvas, per chart — never on `Chart.defaults`. */
export const TREND_CHART_TICK_COLOR = '#000000';
export const TREND_CHART_TICK_FONT = { family: '"Roboto", sans-serif', size: 11, weight: 500 } as const;

const BAR_CORNER_RADIUS = 4;
const CATEGORY_PERCENTAGE = 0.8;
const BAR_PERCENTAGE = 0.92;

function parseIsoDate(value: IsoDate): moment.Moment {
  return moment(value, ISO_DATE_FORMAT, true);
}

/** `Submitted MTD` … `Submitted (custom range)`; the days tile is the same under every period. */
export function trendTileTitle(tile: keyof TrendTiles, period: DashboardPeriod): string {
  const name = TREND_TILE_NAMES[tile];
  if (tile === 'avgDaysToFund') {
    return name;
  }
  return period === DashboardPeriod.CUSTOM ? `${name} (custom range)` : `${name} ${period}`;
}

function sign(value: number): string {
  return value > 0 ? '+' : value < 0 ? '-' : '';
}

/** `+23% vs Jun`, `-100% vs Jun`, `0% vs Jun`; null when either part is missing. ASCII signs. */
export function relativeChangeLabel(percent: number | null, label: string | null): string | null {
  if (percent === null || label === null) {
    return null;
  }
  return `${sign(percent)}${percentLabel(Math.abs(percent), TREND_CHANGE_LABEL_EDGES)} ${label}`;
}

/** `-1.9 days vs Jun`, `+2.0 days …`, `0.0 days …`; null when either part is missing. ASCII signs. */
export function daysChangeLabel(days: number | null, label: string | null): string | null {
  if (days === null || label === null) {
    return null;
  }
  return `${sign(days)}${Math.abs(days).toFixed(1)} days ${label}`;
}

/** Headline in neutral ink; the change line is banded on `changePercent`, and labelled in days for the days tile. */
export function toTrendTileVm(
  tile: keyof TrendTiles,
  tiles: TrendTiles,
  period: DashboardPeriod,
  label: string | null,
): TrendTileVm {
  const title = trendTileTitle(tile, period);
  const tier = trendTier(tiles[tile].changePercent, TREND_TILE_DIRECTIONS[tile]);
  if (tile === 'avgDaysToFund') {
    const days = tiles.avgDaysToFund;
    return {
      title,
      valueLabel: days.value === null ? NO_VALUE_LABEL : days.value.toFixed(1),
      changeLabel: daysChangeLabel(days.changeDays, label),
      tier,
    };
  }
  const count = tiles[tile];
  return {
    title,
    valueLabel: `${count.value}`,
    changeLabel: relativeChangeLabel(count.changePercent, label),
    tier,
  };
}

export function toTrendTileVms(tiles: TrendTiles, period: DashboardPeriod, label: string | null): TrendTileVm[] {
  return TREND_TILE_ORDER.map((tile) => toTrendTileVm(tile, tiles, period, label));
}

function isWholeMonth(from: moment.Moment, to: moment.Moment): boolean {
  return from.date() === 1 && to.isSame(from.clone().add(1, 'month'), 'day');
}

/**
 * `Feb`; `Q3 2025`; `2025`; `Jan 2025` once the chart holds more than 12 buckets; a Custom month partial at either
 * end reads its dates (`Jan 15–31`, `Jun 10`). MTD's own month is partial by construction and reads as a month.
 */
export function bucketLabel(
  bucket: TrendBucket,
  unit: TrendBucketUnit,
  bucketCount: number,
  period: ConversionWirePeriod,
): string {
  const from = parseIsoDate(bucket.from);
  switch (unit) {
    case TrendBucketUnit.QUARTER:
      return `Q${from.quarter()} ${from.year()}`;
    case TrendBucketUnit.YEAR:
      return `${from.year()}`;
    default: {
      const year = bucketCount > MONTH_LABEL_YEAR_THRESHOLD ? ` ${from.year()}` : '';
      const to = parseIsoDate(bucket.to);
      if (period !== ConversionWirePeriod.CUSTOM || isWholeMonth(from, to)) {
        return `${from.format('MMM')}${year}`;
      }
      const last = to.clone().subtract(1, 'day');
      const days = last.isSame(from, 'day')
        ? from.format('MMM D')
        : `${from.format('MMM D')}${DAY_RANGE_SEPARATOR}${last.date()}`;
      return year ? `${days},${year}` : days;
    }
  }
}

/** One line under the chart title, per bucket unit; a truncated Custom range says so instead of naming its dates. */
export function chartSubtitle(
  unit: TrendBucketUnit,
  period: ConversionWirePeriod,
  range: DashboardDateRange | null,
  truncated: boolean,
): string {
  switch (unit) {
    case TrendBucketUnit.QUARTER:
      return 'Quarterly volume · last 4 quarters';
    case TrendBucketUnit.YEAR:
      return 'Year to date · vs last year';
    default: {
      if (period !== ConversionWirePeriod.CUSTOM) {
        return 'Monthly volume · last 6 months';
      }
      if (truncated) {
        return `Monthly volume · last ${MAX_TREND_CHART_BUCKETS} months of the range`;
      }
      const dates = formatDateRange(range);
      return dates ? `Monthly volume · ${dates}` : 'Monthly volume';
    }
  }
}

/** The echo's exclusive `to` back to the filter bar's inclusive last day. */
function inclusiveRange(metrics: TrendMetrics): DashboardDateRange {
  return {
    from: metrics.period.from,
    to: parseIsoDate(metrics.period.to).subtract(1, 'day').format(ISO_DATE_FORMAT),
  };
}

function chartOptions(): ChartConfiguration<'bar'>['options'] {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: TREND_CHART_TICK_COLOR, font: { ...TREND_CHART_TICK_FONT } },
      },
      y: { display: false, beginAtZero: true },
    },
  };
}

export function toTrendChartVm(metrics: TrendMetrics): TrendChartVm {
  const { buckets, bucketUnit, comparison } = metrics;
  return {
    title: TREND_CHART_TITLE,
    subtitle: chartSubtitle(bucketUnit, comparison.period, inclusiveRange(metrics), metrics.bucketsTruncated),
    data: {
      labels: buckets.map((bucket) => bucketLabel(bucket, bucketUnit, buckets.length, comparison.period)),
      datasets: TREND_SERIES.map((series) => ({
        label: TREND_SERIES_LABELS[series],
        data: buckets.map((bucket) => bucket[series]),
        backgroundColor: TREND_SERIES_COLORS[series],
        hoverBackgroundColor: TREND_SERIES_COLORS[series],
        borderRadius: BAR_CORNER_RADIUS,
        categoryPercentage: CATEGORY_PERCENTAGE,
        barPercentage: BAR_PERCENTAGE,
      })),
    },
    options: chartOptions(),
    isEmpty: buckets.every((bucket) => TREND_SERIES.every((series) => bucket[series] === 0)),
    emptyMessage: metrics.bucketsTruncated ? TREND_CHART_TRUNCATED_EMPTY_MESSAGE : TREND_CHART_EMPTY_MESSAGE,
    legend: TREND_SERIES.map((series) => ({ series, label: TREND_SERIES_LABELS[series] })),
  };
}
